import { createContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import {
  BundleHandlers,
  QuestionBundleDto,
  QuestionDto,
  QuestionHandlers,
  QuestionnaireTemplateDto,
} from '../../Types/QuestionnaireT';
import { QuestionnaireTemplateS } from '../QuestionnaireTemplateS';
import { QuestionBundleS } from '../QuestionBundleS';

interface Props {
  loading: boolean;
  insertingBundle: boolean;
  template: QuestionnaireTemplateDto | null;
  patchTemplate: (patch: Partial<QuestionnaireTemplateDto>) => Promise<boolean>;
  bundleHandlers: BundleHandlers;
  questionHandlers: QuestionHandlers;
}

export const QuestionnaireTemplateContext = createContext<Props>({} as Props);

export const QuestionnaireTemplateContextProviderV2 = () => {
  const { templateName } = useParams();
  const [loading, setLoading] = useState(false);
  const [insertingBundle, setInsertingBundle] = useState(false);
  const [template, setTemplate] = useState<QuestionnaireTemplateDto | null>(null);
  const { id: templateId } = template ?? {};

  useEffect(() => {
    if (templateName) {
      setLoading(true);
      QuestionnaireTemplateS.fetchTemplateByName(templateName)
        .then(setTemplate)
        .finally(() => setLoading(false));
    }
  }, [templateName]);

  const patchTemplate = async (patch: Partial<QuestionnaireTemplateDto>) => {
    if (templateId) {
      const resJSON = await QuestionnaireTemplateS.editTemplate(templateId, patch);
      if (resJSON) {
        setTemplate(resJSON);
        return true;
      }
    }
    return false;
  };

  const insertQuestion = async (bundleId: string, index?: number) => {
    const resJSON = await QuestionBundleS.insertQuestion(bundleId, index);
    if (resJSON && template) {
      setTemplate(QuestionBundleS.handleBundleChange(template, resJSON));
      return true;
    }
    return false;
  };

  const shiftQuestion = async (bundleId: string, questionId: string, shift: number) => {
    if (template) {
      const bundle = template.questionBundles.find((qb) => qb.id === bundleId);
      if (bundle) {
        const idx = bundle.questions.findIndex((q) => q.id === questionId);
        const targetQuestionId = bundle.questions[idx + shift].id;
        return moveQuestion(bundleId, questionId, targetQuestionId);
      }
    }
    return false;
  };

  const moveQuestion = async (
    bundleId: string,
    questionId: string,
    targetQuestionId: string,
    originBundleId?: string,
  ) => {
    if (originBundleId) setInsertingBundle(true);
    const resJSON = await QuestionBundleS.moveQuestion(bundleId, questionId, targetQuestionId, originBundleId);
    if (originBundleId) setInsertingBundle(false);
    if (resJSON && template) {
      setTemplate(resJSON as QuestionnaireTemplateDto);
      return true;
    }
    return false;
  };

  const duplicateQuestion = async (bundleId: string, questionId: string, index?: number) => {
    const resJSON = await QuestionBundleS.duplicateQuestion(bundleId, questionId, index);
    if (resJSON && template) {
      setTemplate(QuestionBundleS.handleQuestionDuplicate(template, bundleId, resJSON));
      return true;
    }
    return false;
  };

  const insertBundle = async (index?: number) => {
    if (templateId) {
      setInsertingBundle(true);
      const resJSON = await QuestionnaireTemplateS.insertBundle(templateId, index);
      setInsertingBundle(false);
      if (resJSON) {
        setTemplate(resJSON);
        return true;
      }
    }
    return false;
  };

  const moveBundle = async (bundleId: string, targetBundleId: string) => {
    if (templateId) {
      setInsertingBundle(true);
      const resJSON = await QuestionnaireTemplateS.moveBundle(templateId, bundleId, targetBundleId);
      setInsertingBundle(false);
      if (resJSON) {
        setTemplate(resJSON);
        return true;
      }
    }
    return false;
  };

  const deleteBundle = async (bundleId: string) => {
    if (templateId) {
      const removed = await QuestionnaireTemplateS.removeBundle(templateId, bundleId);
      if (removed && template) {
        setTemplate(QuestionBundleS.handleBundleDelete(template, bundleId));
        return true;
      }
    }
    return false;
  };

  const editQuestion = async (questionId: string, patch: Partial<QuestionDto>) => {
    const resJSON = await QuestionBundleS.editQuestion(questionId, patch);
    if (resJSON && template) {
      setTemplate(QuestionBundleS.handleQuestionChange(template, resJSON));
      return true;
    }
    return false;
  };

  const handleQuestionChange = (question: QuestionDto) => {
    if (template) {
      setTemplate(QuestionBundleS.handleQuestionChange(template, question));
    }
  };

  const deleteQuestion = async (bundleId: string, questionId: string): Promise<boolean> => {
    const removed = await QuestionBundleS.deleteQuestion(bundleId, questionId);
    if (removed && template) {
      setTemplate(QuestionBundleS.handleQuestionDelete(template, bundleId, questionId));
      return true;
    }
    return false;
  };

  const editBundle = async (bundleId: string, patch: Partial<QuestionBundleDto>) => {
    const resJSON = await QuestionBundleS.editBundle(bundleId, patch);
    if (resJSON && template) {
      setTemplate(QuestionBundleS.handleBundleChange(template, resJSON));
      return true;
    }
    return false;
  };

  const value = useMemo(
    () => ({
      loading,
      insertingBundle,
      template,
      patchTemplate,
      bundleHandlers: {
        insertBundle,
        editBundle,
        moveBundle,
        deleteBundle,
      },
      questionHandlers: {
        insertQuestion,
        moveQuestion,
        shiftQuestion,
        editQuestion,
        handleQuestionChange,
        duplicateQuestion,
        deleteQuestion,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, insertingBundle, template],
  );

  return (
    <QuestionnaireTemplateContext.Provider value={value}>
      <Outlet />
    </QuestionnaireTemplateContext.Provider>
  );
};
